/* eslint-disable ext-rules/match */
import BackendApi from '../api/backendApi';
import { User } from '../shared/authentication/User.types';
import EventStore from '../shared/components/websocket/eventStore';
import { errorToastr } from '../shared/utility/toastr';
import { WEBSOCKET_ACTIONS } from '../shared/components/websocket/WebsocketWorker.types';
import { guard } from '../shared/utility/guard';
import { STORAGE_CALC_JOB_ID_KEY } from '../shared/components/common/constants';

export const getPrintDocumentCallback = async (
    backendApi: BackendApi | undefined,
    eventStore: EventStore | undefined,
    printOption: string,
    onPrintLoadingChange: (b: boolean) => void,
    user: User,
    market: string,
    airflowUnit: string,
    productVariant: string,
    printRequestTimeout: number = 40000,
    callbackTimeout: number = 60000
) => {
    if (!backendApi || !user || !market) {
        return;
    }

    const maybeCalcJobId = sessionStorage.getItem(STORAGE_CALC_JOB_ID_KEY);
    if (!maybeCalcJobId) {
        return;
    }
    const calcJobId = maybeCalcJobId;

    onPrintLoadingChange(true);

    const userIdGuest = `${user?.userId}-guest`;
    let timeoutId: NodeJS.Timeout;

    const convertedProductVariant = productVariant.replace(/[_-]/g, '');

    await backendApi
        .sendPrintRequest(
            market,
            airflowUnit,
            printOption,
            calcJobId,
            convertedProductVariant,
            printRequestTimeout
        )
        .then((responseJobId) => {
            const apiCallPromise = (async () => {
                eventStore?.addEventListener(
                    `${userIdGuest}.printResult`,
                    (event: MessageEvent) =>
                        printResultCallback(
                            event,
                            backendApi,
                            eventStore,
                            printOption,
                            responseJobId,
                            user,
                            convertedProductVariant,
                            onPrintLoadingChange,
                            () => clearTimeout(timeoutId)
                        )
                );
                eventStore?.addEventListener(
                    `${userIdGuest}.printFailed`,
                    (event: MessageEvent) =>
                        printFailedCallback(
                            event,
                            eventStore,
                            responseJobId,
                            user,
                            onPrintLoadingChange,
                            () => clearTimeout(timeoutId)
                        )
                );
            })();

            const timeoutPromise = new Promise<void>(
                (resolve) =>
                    (timeoutId = setTimeout(() => {
                        failOrTimeoutCallback();
                        resolve();
                    }, callbackTimeout))
            );

            Promise.race([apiCallPromise, timeoutPromise]).catch((error) => {
                failOrTimeoutCallback();
                errorToastr(error);
            });

            const failOrTimeoutCallback = () => {
                eventStore?.removeEventListener(`${userIdGuest}.printResult`);
                eventStore?.removeEventListener(`${userIdGuest}.printFailed`);
                onPrintLoadingChange(false);
                errorToastr('Print request timed out');
            };
        })
        .catch((error) => {
            onPrintLoadingChange(false);
            errorToastr(error);
        });
};

export const printResultCallback = async (
    event: MessageEvent,
    backendApi: BackendApi,
    eventStore: EventStore,
    activePrintOption: string,
    jobId: string,
    user: User,
    productVariant: string,
    setIsPrintLoading: (b: boolean) => void,
    clearStartPrintTimeout: () => void = () => {}
) => {
    if (isExpectedEvent(event, jobId, WEBSOCKET_ACTIONS.PRINT_RESULT)) {
        clearStartPrintTimeout();
        eventStore?.removeEventListener(`${user?.userId}-guest.printResult`);
        eventStore?.removeEventListener(`${user?.userId}-guest.printFailed`);

        await backendApi
            ?.getPrintDocument(jobId, activePrintOption, productVariant)
            .then(() => {
                setIsPrintLoading(false);
            })
            .catch((error) => {
                errorToastr(error);
                setIsPrintLoading(false);
            });
    }
};

export const printFailedCallback = async (
    event: MessageEvent,
    eventStore: EventStore,
    jobId: string,
    user: User,
    setIsPrintLoading: (b: boolean) => void,
    clearStartPrintTimeout: () => void = () => {}
) => {
    if (isExpectedEvent(event, jobId, WEBSOCKET_ACTIONS.PRINT_FAILED)) {
        eventStore?.removeEventListener(`${user?.userId}-guest.printFailed`);
        eventStore?.removeEventListener(`${user?.userId}-guest.printResult`);
        clearStartPrintTimeout();
        errorToastr(event.data.message.message);
        setIsPrintLoading(false);
    }
};

const isExpectedEvent = (
    event: MessageEvent,
    expectedJobId: string,
    expectedTask: WEBSOCKET_ACTIONS
) => {
    const { task, message } = event.data;
    return task === expectedTask && message.jobId == expectedJobId;
};

export const addListenersIfOngoingPrintJob = async (
    backendApi: BackendApi | undefined,
    eventStore: EventStore | undefined,
    jobId: string | null,
    user: User,
    productVariant: string,
    setIsPrintLoading: (b: boolean) => void,
    timeout: number = 60000
) => {
    if (!backendApi || !jobId) {
        return;
    }

    const printStatus = await backendApi.getPrintStatus(jobId);

    let missingValues: boolean = false;

    const [documentType, __, ___] = guard(
        () => {
            missingValues = true;
        },
        printStatus.documentType,
        printStatus.jobId,
        printStatus.status
    ) as [string, string, string];

    if (missingValues) {
        return;
    }

    if (printStatus?.status?.toLowerCase() == 'ongoing') {
        setIsPrintLoading(true);

        const userIdGuest = `${user?.userId}-guest`;
        let timeoutId: NodeJS.Timeout;

        const apiCallPromise = (async () => {
            eventStore?.addEventListener(
                `${userIdGuest}.printResult`,
                (event: MessageEvent) =>
                    printResultCallback(
                        event,
                        backendApi,
                        eventStore,
                        documentType,
                        jobId,
                        user,
                        productVariant,
                        setIsPrintLoading,
                        () => clearTimeout(timeoutId)
                    )
            );
            eventStore?.addEventListener(
                `${userIdGuest}.printFailed`,
                (event: MessageEvent) =>
                    printFailedCallback(
                        event,
                        eventStore,
                        jobId,
                        user,
                        setIsPrintLoading,
                        () => clearTimeout(timeoutId)
                    )
            );
        })();

        const timeoutPromise = new Promise<void>(
            (resolve) =>
                (timeoutId = setTimeout(() => {
                    failOrTimeoutCallback();
                    resolve();
                }, timeout))
        );

        Promise.race([apiCallPromise, timeoutPromise]).catch(() => {
            failOrTimeoutCallback();
        });

        const failOrTimeoutCallback = () => {
            eventStore?.removeEventListener(`${userIdGuest}.printResult`);
            eventStore?.removeEventListener(`${userIdGuest}.printFailed`);
            setIsPrintLoading(false);
            errorToastr('Print request timed out');
        };
    }
};

export type PrintStatus = {
    jobId?: string;
    status?: string;
    documentType?: string;
};
